// "Changed today" — zone identity phase 6 (docs/reference/zone_and_settings_changes.md §5).
//
// Some changes reach the stored figures only at the nightly run: a zone's TYPE (its
// figures move to the new type) and the settings that feed the figures. The backend
// keeps a register of them (GET /api_db/changes_today/, applicaite/changes_today.py);
// this module holds what the screens do with it — shared by production and the demo.
//
// Pure (unit-tested).

import { i18n, currentLanguage } from '@/i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

export const ZONE_TYPE_NAMES = {
  1: 'Entrance Zone', 2: 'Exit Zone', 3: 'Combined Entrance Exit Zone',
  4: 'Checkout Waiting Zone', 5: 'Checkout Interaction Zone',
  6: 'Product Zone', 7: 'Demo Zone', 8: 'Display Zone',
  9: 'Info Zone', 10: 'Service Zone', 11: 'Pick Up Zone',
  12: 'Info Waiting Zone', 13: 'Service Waiting Zone', 14: 'Pick Up Waiting Zone',
  15: 'Info Desk Zone', 16: 'Service Desk Zone', 17: 'Pick Up Desk Zone',
  999: 'Cash Register Zone',
}

// Widget field-name token per type ("product_zone_visitors_counts", "best_display_zone_…").
export const TYPE_TOKENS = { 6: 'product', 7: 'demo', 8: 'display', 9: 'info', 10: 'service', 11: 'pick_up' }

// The notices are read at the moment they're shown (getters), so they follow the
// portal's language; English is the text it always was. Type names arrive as the
// English data names (ZONE_TYPE_NAMES) and are shown in the reader's language.
const t = (key, params) => i18n.global.t(key, params || {})
const typeLabel = (typeName) => {
  if (currentLanguage() === 'en') return typeName
  if (typeName === 'zone of another type') return t('changes.otherType')
  return zoneTypeLabel(typeName)
}

export const NOTICES = {
  get zonePage() { return t('changes.zonePage') },
  // In the NEW type's section (Explore): the zone is listed there, its figures are not yet.
  movedInto: (name, typeName) => t('changes.movedInto', { name, typeName: typeLabel(typeName) }),
  // A goal on that zone: kept, flagged (decision D8).
  get goal() { return t('changes.goal') },
  // A line in an in-progress action's timeline.
  actionLine: (dateText, typeName) => t('changes.actionLine', { dateText, typeName: typeLabel(typeName) }),
  // Store health, the briefing, and the store-visitors figures (6.4).
  get storeValues() { return t('changes.storeValues') },
  // A flow-page card in a history view (6.6): figures kept, the old type named.
  flowCard: (oldTypeName) => t('changes.flowCard', { typeName: typeLabel(oldTypeName) }),
  // Settings that reach the stored figures only tonight (6.7).
  settings: {
    get money() { return t('changes.settings.money') },
    get opening_hours() { return t('changes.settings.openingHours') },
    get thresholds() { return t('changes.settings.thresholds') },
  },
}

// Entrance-like types: a change to or from one of these changes store footfall itself.
export const ENTRANCE_TYPES = [1, 2, 3]

// The register's entry for a zone that changed type today, or null. Looked up by key;
// `name` as a fallback for data without keys (the demo).
export function typeChangeOf(register, { key = null, name = null } = {}) {
  const zones = (register && register.zones) || {}
  // A record with a key is matched by its key only: another zone may share the name.
  if (key != null) return zones[String(key)] || null
  if (name) return Object.values(zones).find((z) => z && z.name === name) || null
  return null
}

// Zones that changed INTO this type today: listed under it already, figures tonight.
export function movedInto(register, typeId) {
  return Object.values((register && register.zones) || {})
    .filter((z) => z && Number(z.to) === Number(typeId))
}

// Is this (finding, suggestion, goal, …) about a zone that changed type today?
export function aboutTypeChangedZone(register, { key = null, name = null } = {}) {
  return !!typeChangeOf(register, { key, name })
}

// Did any zone change type today? (Store health + briefing notice.)
export function anyTypeChange(register) {
  return Object.keys((register && register.zones) || {}).length > 0
}

// Did a zone change type to or from an entrance type today? (Store-visitors notice.)
export function entranceTypeChange(register) {
  return Object.values((register && register.zones) || {}).some((z) => z
    && (ENTRANCE_TYPES.includes(Number(z.from)) || ENTRANCE_TYPES.includes(Number(z.to))))
}

// Was a setting of this kind changed today (6.7)? With `zoneKey`: for that zone, or a
// company-wide change (an entry without zones applies to every zone).
export function settingChanged(register, kind, zoneKey = null) {
  const e = register && register.settings && register.settings[kind]
  if (!e) return false
  const zones = e.zones || []
  if (zoneKey == null || !zones.length) return true
  return zones.map(String).includes(String(zoneKey))
}
